import type { ColumnDef } from "@/lib/columnRegistry";

/**
 * Registry des colonnes de la page Catalogue (V2). Pilote le sélecteur de
 * colonnes (ColumnPickerModal) + l'export XLS. La sélection user est
 * persistée en localStorage (par navigateur) — voir BiensCataloguePage.
 *
 * Chaque colonne a une LARGEUR FIXE en px : la table déborde (scroll
 * horizontal) au lieu de rétrécir les colonnes quand on en ajoute.
 *
 * Note : `i18nLabel` contient des CLÉS i18n (pas du texte FR) — les en-têtes
 * de table, l'export XLS et le ColumnPickerModal passent par `t()`. Réutilise
 * les clés du registry V1 (`biensColumns.ts`) quand le libellé est identique ;
 * sinon une clé dédiée sous `v2.catalogue.columns.*`.
 */
export type CatalogueColumnKey =
  // Visibles par défaut
  | "ref"
  | "projet"
  | "adresse"
  | "dispo"
  | "lifecycle"
  | "publication"
  | "libre"
  | "libreDepuis"
  | "loyer"
  | "surface"
  | "peb"
  // Optionnelles
  | "commune"
  | "codePostal"
  | "region"
  | "chambres"
  | "nbSdb"
  | "etage"
  | "charges"
  | "collaborateur"
  | "renon"
  | "finContrat"
  | "pebFin"
  | "typeBien"
  | "meuble"
  | "companyContract"
  | "cave"
  | "parking"
  | "garage"
  | "pvt";

// Alignement : "Référence / Projet / Adresse" restent à gauche ; TOUTES les
// colonnes à partir de "Statut" sont centrées (décision user 2026-06-02).
export const CATALOGUE_COLUMNS: ColumnDef<CatalogueColumnKey>[] = [
  { key: "ref",         i18nLabel: "biens.reference",              align: "left",   defaultVisible: true,  width: 130 },
  { key: "projet",      i18nLabel: "biens.projet",                 align: "left",   defaultVisible: true,  width: 160 },
  { key: "adresse",     i18nLabel: "common.address",               align: "left",   defaultVisible: true,  width: 230 },
  { key: "dispo",       i18nLabel: "common.status",                align: "center", defaultVisible: true,  width: 120 },
  { key: "lifecycle",   i18nLabel: "v2.nav.pipelineEls",           align: "center", defaultVisible: true,  width: 170 },
  { key: "publication", i18nLabel: "v2.catalogue.columns.publication", align: "center", defaultVisible: true,  width: 180 },
  { key: "libre",       i18nLabel: "v2.catalogue.columns.libre",   align: "center", defaultVisible: true,  width: 100 },
  { key: "libreDepuis", i18nLabel: "v2.catalogue.columns.libreDepuis", align: "center", defaultVisible: true,  width: 90 },
  { key: "loyer",       i18nLabel: "v2.catalogue.columns.loyer",   align: "center", defaultVisible: true,  width: 100 },
  { key: "surface",     i18nLabel: "biens.surface",                align: "center", defaultVisible: true,  width: 100 },
  { key: "peb",         i18nLabel: "biens.columns.classePeb",      align: "center", defaultVisible: true,  width: 70 },
  // Optionnelles — centrées aussi (toutes situées après "Statut").
  { key: "commune",     i18nLabel: "common.city",                  align: "center", defaultVisible: false, width: 130 },
  { key: "codePostal",  i18nLabel: "biens.columns.codePostal",     align: "center", defaultVisible: false, width: 100 },
  { key: "region",      i18nLabel: "common.region",                align: "center", defaultVisible: false, width: 110 },
  { key: "chambres",    i18nLabel: "biens.columns.nbChambres",     align: "center", defaultVisible: false, width: 90 },
  { key: "nbSdb",       i18nLabel: "biens.columns.nbSdb",          align: "center", defaultVisible: false, width: 100 },
  { key: "etage",       i18nLabel: "biens.columns.etage",          align: "center", defaultVisible: false, width: 80 },
  { key: "charges",     i18nLabel: "biens.columns.charges",        align: "center", defaultVisible: false, width: 100 },
  { key: "collaborateur", i18nLabel: "common.collaborateur",       align: "center", defaultVisible: false, width: 160 },
  { key: "renon",       i18nLabel: "biens.columns.renonRecuLe",    align: "center", defaultVisible: false, width: 120 },
  { key: "finContrat",  i18nLabel: "biens.columns.finDeContrat",   align: "center", defaultVisible: false, width: 120 },
  { key: "pebFin",      i18nLabel: "biens.columns.pebFinLe",       align: "center", defaultVisible: false, width: 110 },
  { key: "typeBien",    i18nLabel: "v2.catalogue.columns.typeBien", align: "center", defaultVisible: false, width: 130 },
  { key: "meuble",          i18nLabel: "biens.columns.meuble",     align: "center", defaultVisible: false, width: 90 },
  { key: "companyContract", i18nLabel: "v2.catalogue.columns.companyContract", align: "center", defaultVisible: false, width: 140 },
  { key: "cave",            i18nLabel: "v2.catalogue.columns.cave",    align: "center", defaultVisible: false, width: 130 },
  { key: "parking",         i18nLabel: "v2.catalogue.columns.parking", align: "center", defaultVisible: false, width: 130 },
  { key: "garage",          i18nLabel: "v2.catalogue.columns.garage",  align: "center", defaultVisible: false, width: 130 },
  // Même badge que la préparation Publication (photos/vidéo/textes) — demande
  // Thibaud 2026-08-20 : le voir directement dans le Catalogue sans ouvrir
  // Publication.
  { key: "pvt",             i18nLabel: "v2.catalogue.columns.pvt",     align: "center", defaultVisible: false, width: 100 },
];
